import {
  CheckboxController,
  Collapse,
  Text,
} from "@pushpress/shared-components";
import { useContext, useMemo } from "react";
import { type Control, useWatch, useFieldArray, Controller } from "react-hook-form";
import { billsOnOptions, clientStatusOptions, intervalTypeOptions } from "../PlanDrawer.utils";
import { PlanCategory, PlanType } from "../../../../apis/subscriptionApi";
import { PlanForm } from "../PlanDrawer.types";
import { getCurrencySymbol } from "../../../../utils/currencyUtils";
import { AppContext } from "../../../../context/app";
import { useExperiment } from "../../../../hooks/useExperiment";
import BillingAdvancedOptionsCollapse from "./BillingAdvancedOptionsCollapse";
import { Button,Input,Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@pushpress/pushpress-ui";
import { sortByName } from "../../../../utils/sortUtils";
import { usePlanCategoriesQuery } from "../../../../apis/subscriptionApi.hooks";
import { useGetTaxesQuery } from "../../../../components/controllers/TaxSelect/TaxSelect-generated.hooks";
import { Tax } from "../../../../apis/clientApi";

interface BillingModalSectionProps {
  control: Control<PlanForm>;
  planType: PlanType;
}

const BillingModalSection = (props: BillingModalSectionProps) => {

  const { control, planType } = props;

  const { client } = useContext(AppContext);

  const { useExperimentValue } = useExperiment();

  const fixedBillingDayOfMonthFeatureFlag = useExperimentValue(
    "plan-prorate-landing-pages",
    false,
  );

  // Use useFieldArray for billing options
  const { fields, append, remove } = useFieldArray({
    control,
    name: "prices",
  });

  const { data: categoriesData } = usePlanCategoriesQuery({
    staleTime: Infinity,
  });

  const { data: taxesData } = useGetTaxesQuery({
    variables: {
      getTaxes: {
        clientUuid: client.uuid,
      },
    },
  });


  const categoryOptions = useMemo(() => {
    if (!categoriesData?.data) {
      return [];
    }
    return [...categoriesData.data].sort(sortByName) as PlanCategory[];
  }, [categoriesData]);

  const taxes = useMemo(() => {
    if (!taxesData?.getTaxes) {
      return [];
    }
    return [...taxesData.getTaxes].sort(sortByName) as Tax[];
  }, [taxesData]);

  const prices = useWatch({ control, name: "prices" });
  
  // Helper to get currency symbol
  const currencySymbol = getCurrencySymbol(client.currencyIso);

  return (
    <div>
      <Text
        component="h1"
        variant="heading-4"
        weight="600"
        color="wolf-800"
        className="mt-4"
      >
        Basic Information & Pricing
      </Text>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4"> 
          <div className="mt-3 tw-grid tw-items-center tw-gap-1.5">
            <label htmlFor="name">
              Plan name *
            </label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="e.g. Unlimited membership"
                  {...field}
                  required
                  name="name"
                />
              )}
            />
          </div>
        <div className="mt-3">
          <div className="tw-grid tw-items-center tw-gap-1.5">
            <label htmlFor="clientStatus">
              Client status *
            </label>
            <Controller
              name="clientStatus"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value?.value || ""}
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                  required
                  name="clientStatus"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {clientStatusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          </div>
          <div className="mt-3 tw-grid tw-items-center tw-gap-1.5">
            <label htmlFor="planCategory">
              Category *
            </label>
          <Controller
            name="planCategory"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value?.uuid || ""}
                onValueChange={(value) => {
                  const selectedCategory = categoryOptions.find(cat => cat.uuid === value);
                  field.onChange(selectedCategory);
                }}
                required
                name="planCategory"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {categoryOptions.map((option) => (
                      <SelectItem key={option.uuid} value={option.uuid}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              )}
            />  
          </div>
        </div>
        <div className="tw-mt-3 tw-grid tw-items-center tw-gap-1.5">
          <Text
            component="h2"
            variant="heading-5"
            weight="600"
            color="wolf-800"
            className="tw-mb-2 mt-4"
          >
            Pricing
          </Text>
        <Text
          component="p"
          variant="body-md"
          weight="400"
          color="wolf-800"
          className="tw-mb-2"
          >
            You can add multiple pricing options - likely monthly and annual - to give members the flexibility and encourage longer commitments.
          </Text>
        </div>
      {fields.map((field, idx) => {

        const option: Partial<PlanForm['prices'][0]> =
          prices?.[idx] || {};

        return (
          <div
            key={field.id}
            className="tw-bg-white tw-rounded-lg tw-border tw-border-wolf-200 tw-p-6 tw-mt-4 tw-relative"
          >
            
            {fields.length > 1 && (
              <button
                type="button"
                onClick={() => remove(idx)}
                className="tw-absolute tw-top-2 tw-right-2 tw-text-wolf-400 hover:tw-text-red-500 tw-text-lg tw-font-bold tw-z-10"
                aria-label="Remove billing option"
              >
                ×
              </button>
            )}
            <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 tw-w-full">
              {/* Bill every */}
              {planType !== "session-pack" && (
                <div>
                  <div className="tw-flex-1 tw-grid tw-items-center tw-gap-1.5">
                    <label htmlFor="billEveryCount">
                      Bill every *
                    </label>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2">
                      <Controller
                        name={`prices.${idx}.billEveryCount`}
                        control={control}
                        render={({ field }) => (
                        <Input
                          style={{
                            maxWidth: "100px",
                          }}
                          placeholder={planType === "recurring" ? "Bill every" : "Expire plan in"}
                          type="number"
                          min={1}
                          autoComplete="off"
                          {...field}
                          required
                          name={`prices.${idx}.billEveryCount`}
                          />
                        )}
                        />
                      <Controller
                        name={`prices.${idx}.billEveryInterval`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value?.value || ""}
                            onValueChange={(value) => {
                              const selectedInterval = intervalTypeOptions.find(interval => interval.value === value);
                              field.onChange(selectedInterval);
                            }}
                            required
                            name={`prices.${idx}.billEveryInterval`}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Interval" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {intervalTypeOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          )}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* Price */}
              <div className="tw-flex-1">
                <div className="tw-grid tw-items-center tw-gap-1.5">
                  <label htmlFor="price">
                    Price *
                  </label>
                  <div className="tw-flex tw-items-center tw-gap-0">
                    <div className="tw-w-[28px] tw-h-9 tw-flex tw-items-center 
                    tw-justify-center tw-rounded-l-md tw-bg-wolf-100 tw-border tw-border-wolf-200 tw-border-r-0">
                      <span className="tw-text-sm tw-text-wolf-500">
                        {currencySymbol}
                      </span>
                    </div>
                    <Controller
                      name={`prices.${idx}.price`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="number"
                          min={0}
                          autoComplete="off"
                          {...field}
                          required
                          name={`prices.${idx}.price`}
                          className="tw-rounded-l-none tw-flex-1"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              {planType == "recurring" &&
                prices[idx]?.billEveryInterval?.value == "month" &&
                fixedBillingDayOfMonthFeatureFlag && (
                  <div className="tw-flex-1">
                    <div className="tw-grid tw-items-center tw-gap-1.5">
                      <label htmlFor="fixedBillingDayOfMonth">
                        Bills on
                      </label>
                      <Controller
                        name={`prices.${idx}.fixedBillingDayOfMonth`}
                        control={control}
                        render={({ field }) => (
                          <Select
                          value={field.value?.value?.toString() || "-1"}
                          onValueChange={(value) => {
                            if (value === "-1") {
                              field.onChange(null);
                            } else {
                              field.onChange(parseInt(value));
                            }
                          }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Bills on" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {billsOnOptions.map((option) => (
                                    <SelectItem key={option.value.toString()} value={option.value.toString()}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                  </div>
                )}
            </div>
            {/* Checkboxes */}
            {planType === "recurring" && (
              <>
                <CheckboxController
                  control={control}
                  name={`prices.${idx}.recursForever`}
                  label="Recurs forever"
                  className="mt-1"
                />
                <Collapse in={!prices[idx]?.recursForever}>
                  <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-3">
                    <label htmlFor="occurrences">
                      Occurrences
                    </label>
                    <Controller
                      name={`prices.${idx}.occurrences`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min={2}
                          autoComplete="off"
                          placeholder="Occurrences"
                        />
                      )}
                    />
                  </div>
                </Collapse>
              </>
            )}
            
            <CheckboxController
              control={control}
              name={`prices.${idx}.hasSetupFee`}
              label="Setup fee (one-time)"
              className="tw-mt-4"
            />
            {/* Setup fee input (conditional) */}
            <Collapse in={option.hasSetupFee}>
            <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-3">
                  <label htmlFor="setupFee">
                    Setup fee *
                  </label>
                  <div className="tw-flex tw-items-center tw-gap-0">
                    <div className="tw-w-[28px] tw-h-9 tw-flex tw-items-center 
                    tw-justify-center tw-rounded-l-md tw-bg-wolf-100 tw-border tw-border-wolf-200 tw-border-r-0">
                      <span className="tw-text-sm tw-text-wolf-500">
                        {currencySymbol}
                      </span>
                    </div>
                    <Controller
                      name={`prices.${idx}.setupFee`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="number"
                          min={0}
                          autoComplete="off"
                          {...field}
                          required
                          name={`prices.${idx}.setupFee`}
                          className="tw-rounded-l-none tw-flex-1"
                        />
                      )}
                    />
                  </div>
                </div>
            </Collapse>
          </div>
        );
      })}
      {/* Add another billing option button */}
      <div className="tw-mt-4">
        <Button
          variant="outline"
          onClick={() =>
            append({
              price: 0,
              billEveryCount: 1,
              billEveryInterval: intervalTypeOptions[2],
              recursForever: true,
              hasSetupFee: false,
              setupFee: 0,
            })
          }
        >
          + Add another billing option
        </Button>
      </div>
      {/* Tax select */}
      <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-4">
        <label htmlFor="taxRate">
          Tax rate
        </label>
        <Controller
          name="taxRate"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value?.uuid || ""}
              onValueChange={(value) => {
                if (value === "none") { 
                  field.onChange(null);
                } else {
                  const selectedTax = taxes.find(tax => tax.uuid === value);
                  field.onChange(selectedTax);
                }
              }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a tax" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="none">Select a tax</SelectItem>
                    {taxes.map((tax) => (
                      <SelectItem key={tax.uuid} value={tax.uuid}>
                        {tax.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
      </div>
      {/* More settings section */}
      {/* Advanced Options Collapse */}      
      <BillingAdvancedOptionsCollapse control={control} />
    </div>
  );
};

export default BillingModalSection;
